'use client';

import { ReactNode, useContext, useEffect, useState } from 'react';

import { JourneyType } from '@bytel/leviator';
import { CartContext } from '@bytel/leviator/react';
import {
    Alert,
    Button,
    ButtonVariant,
    Column,
    Columns,
    Spacer,
    SpacerSize,
    StatusState,
    Text,
    TextLevels,
    TypographyAlign,
    TypographyBold,
} from '@bytel/trilogy-react';

import { getAppConfig } from '@services/config';
import { PlanPrice } from '@services/products';

import { MobilePlanType } from '@app-types/plan';

import { RocCard } from '@components/roc-card';
import { SSIBlock } from '@components/ssi-block';

import { useMarketings } from '@hooks/use-marketings';

import { partials } from '@helpers/partials';
import { paths } from '@helpers/path';
import { getNeededElementGencodes } from '@helpers/product';
import { formatTagText } from '@helpers/tracking';
import { sendPlanDetailsTracking } from '@helpers/walls';

import { PlanRange, PlanTypeId } from '@constants/plan';
import { ERROR_CONTEXT } from '@constants/provider';

import {
    EligibilityResults,
    JourneyConfigType,
    getXgboxOffersDetails,
    sendErrorImpressionTracking,
    sendTrackingFromEligibilityMismatchError,
    useFetchSinglePlan,
} from '@eligibility/helpers';
import { ModalPlanDetailsContainer, OfferCard } from '@offer-wall/components';

import { Forecast } from '../forecast';
import { ImageCtaBox } from '../image-cta-box';
import { ByouPlanCard } from './byou-plan-card/byou-plan-card';

type EligibilityResultErrorProps = {
    error: ERROR_CONTEXT;
    address?: ReactNode;
    results: EligibilityResults;
    planId?: string;
    onAddToCart: (gencode: string) => void;
    journeyConfig: JourneyConfigType;
};

export function EligibilityResultError({ error, ...props }: Readonly<EligibilityResultErrorProps>) {
    useEffect(() => {
        sendErrorImpressionTracking(props.journeyConfig, error);
    }, [error]);

    if (error === ERROR_CONTEXT.ERROR) {
        return <TechnicalError {...props} />;
    }

    if (error === ERROR_CONTEXT.NO_OFFER) {
        return <NoOfferError {...props} />;
    }

    if (error === ERROR_CONTEXT.MISMATCH_CONFIG) {
        return <MismatchConfigError {...props} />;
    }

    return null;
}

function MismatchConfigError({
    children,
    results,
    address,
    planId,
    onAddToCart,
    journeyConfig,
}: {
    children?: ReactNode;
    results: EligibilityResults;
    address?: ReactNode;
    planId?: string;
    onAddToCart: (gencode: string) => void;
    journeyConfig: JourneyConfigType;
}) {
    const { addProductsToCartMutation } = useContext(CartContext);
    const [marketingDiscounts] = useMarketings();
    const [offerSelectedForDetails, setOfferSelectedForDetails] = useState<MobilePlanType>();
    const appConfig = getAppConfig();

    const { mobilePlanDetailed, isLoading } = useFetchSinglePlan(planId);

    const openOfferDetails = (offer: MobilePlanType) => {
        sendPlanDetailsTracking(offer);
        setOfferSelectedForDetails(offer);
    };

    const onAddMobilePlanToCart = async (chosenOffer?: MobilePlanType) => {
        if (chosenOffer) {
            sendTrackingFromEligibilityMismatchError(
                journeyConfig,
                `Continuer_avec_ce_forfait_${formatTagText(chosenOffer.name)}`,
            );
            const autoAdd = getNeededElementGencodes(chosenOffer);
            await addProductsToCartMutation.mutateAsync({
                journey: JourneyType.MOBILE_ACQUISITION,
                gencodes: [chosenOffer.gencode, ...autoAdd],
            });
            window.location.assign(paths.cart);
        }
        return new Promise<void>(() => window.location.assign(paths.cart));
    };

    if (isLoading) {
        return null;
    }

    const { has5gOffer, hasXgboxOffer } = getXgboxOffersDetails(results, PlanTypeId.FAI_ELIGIBILITE);

    return (
        <>
            {address}
            {mobilePlanDetailed ? (
                <>
                    <Text
                        level={TextLevels.ONE}
                        typo={[TypographyAlign.TEXT_CENTERED, TypographyBold.TEXT_WEIGHT_SEMIBOLD]}
                    >
                        Vous pouvez continuer avec votre forfait sans engagement
                    </Text>
                    <Spacer size={SpacerSize.NINE} />
                    <Columns className='is-centered'>
                        <Column desktopSize={4} widescreenSize={3}>
                            {mobilePlanDetailed.commercialRange === PlanRange.B_YOU ? (
                                <ByouPlanCard mobilePlan={mobilePlanDetailed} journeyConfig={journeyConfig} />
                            ) : (
                                <OfferCard
                                    offer={mobilePlanDetailed}
                                    showBlockedPlanZone
                                    openOfferDetails={openOfferDetails}
                                    isConnected={false}
                                    isPhoneLoading={false}
                                    isSmallPrice={false}
                                    hatInLine={false}
                                    onAddToCart={() => onAddMobilePlanToCart(mobilePlanDetailed)}
                                    customName={`Forfait ${mobilePlanDetailed.dataEnvelope}`}
                                />
                            )}
                        </Column>
                    </Columns>
                    {offerSelectedForDetails && (
                        <ModalPlanDetailsContainer
                            selectedOffer={offerSelectedForDetails}
                            marketingDiscounts={marketingDiscounts}
                            onClose={() => setOfferSelectedForDetails(undefined)}
                            customPrice={mobilePlanDetailed?.details.price as unknown as PlanPrice}
                        />
                    )}
                </>
            ) : (
                <XgboxContent results={results} onAddToCart={onAddToCart} journeyConfig={journeyConfig} />
            )}
            <Spacer size={SpacerSize.NINE} />
            {mobilePlanDetailed && hasXgboxOffer ? (
                <Columns className=''>
                    <Column className='is-fullheight'>
                        <ImageCtaBox
                            backgroundImage={`${appConfig.assets.cmsImages.url}/original/image/CMS/ecommerce/eligibility/${has5gOffer ? '5g' : '4g'}-box.webp`}
                            title={`La ${has5gOffer ? '5G' : '4G'} box haut débit est disponible chez vous 🚀`}
                            text={
                                <Text level={TextLevels.ONE}>
                                    En plus, elle est <strong>sans engagement</strong> et ça, c’est parfait en attendant
                                    la fibre&nbsp;!
                                </Text>
                            }
                            cta={
                                <Button
                                    className=''
                                    variant={ButtonVariant.PRIMARY}
                                    fullwidth
                                    onClick={() =>
                                        sendTrackingFromEligibilityMismatchError(
                                            journeyConfig,
                                            `Découvrir_la_${has5gOffer ? '5G' : '4G'}_box`,
                                        )
                                    }
                                    href={`/offres-internet/${has5gOffer ? '5g-box' : '4g-box'}`}
                                >
                                    Découvrir la {has5gOffer ? '5G' : '4G'} box
                                </Button>
                            }
                        />
                    </Column>
                    <Column>
                        <Forecast results={results} journeyConfig={journeyConfig} />
                    </Column>
                </Columns>
            ) : (
                <Columns className='is-centered'>
                    <Column size={6}>
                        <Forecast results={results} journeyConfig={journeyConfig} />
                    </Column>
                </Columns>
            )}

            {children}
        </>
    );
}

function XgboxContent({
    results,
    onAddToCart,
    journeyConfig,
}: {
    results: EligibilityResults;
    onAddToCart: (gencode: string) => void;
    journeyConfig: JourneyConfigType;
}) {
    const xgboxCategory = journeyConfig.fallbackMismatchOffers || journeyConfig.offerCategoryCodeToShow;
    const { has5gOffer, hasXgboxOffer } = getXgboxOffersDetails(results, xgboxCategory);

    if (!hasXgboxOffer) {
        return (
            <Text level={TextLevels.ONE} typo={[TypographyAlign.TEXT_CENTERED, TypographyBold.TEXT_WEIGHT_SEMIBOLD]}>
                Vous n&apos;êtes éligible à aucune offre
            </Text>
        );
    }

    const xgboxOffers = Object.values(results.offers)?.[0] ?? [];
    const hasSiblingHat = xgboxOffers.some((o) => o.hat !== undefined);

    return (
        <>
            <Text level={TextLevels.ONE} typo={[TypographyAlign.TEXT_CENTERED, TypographyBold.TEXT_WEIGHT_SEMIBOLD]}>
                Vous pouvez profiter de la {has5gOffer ? '5G' : '4G'} box {has5gOffer ? 'très' : ''} haut débit
            </Text>
            <Spacer size={SpacerSize.NINE} />
            <Columns className='is-centered' multiline>
                {xgboxOffers.map((offer) => (
                    <Column
                        key={`${offer.rangeNg}_${offer.commercialSeries}`}
                        mobileSize={12}
                        tabletSize={6}
                        desktopSize={4}
                        data-cy={`${offer.rangeNg}_${offer.commercialSeries}`}
                    >
                        <RocCard
                            plan={offer.mapToFaiType()}
                            cta={{
                                onClick: () => {
                                    sendTrackingFromEligibilityMismatchError(
                                        journeyConfig,
                                        `Ajouter_au_panier_${formatTagText(offer.name)}`,
                                    );
                                    onAddToCart(offer.sku);
                                },
                                text: 'Ajouter au panier',
                                color: ButtonVariant.CONVERSION,
                            }}
                            hasSiblingHat={hasSiblingHat}
                            showComparatorCheckbox={journeyConfig.showComparatorSelection}
                        />
                    </Column>
                ))}
            </Columns>
        </>
    );
}

function NoOfferError({
    address,
    results,
    journeyConfig,
}: {
    address?: ReactNode;
    results: EligibilityResults;
    journeyConfig: JourneyConfigType;
}) {
    return (
        <>
            {address}
            <Columns className='is-centered'>
                <Column size={6}>
                    <Alert
                        title='Ce logement ne semble malheureusement éligible à aucune offre'
                        description='Vous pouvez modifier l’adresse pour tester un autre logement, ou bien renseigner vos coordonnées pour être prévenu de l’arrivée de la fibre. '
                        status={StatusState.ERROR}
                        display
                    />
                    <Spacer size={SpacerSize.NINE} />
                </Column>
            </Columns>
            <Columns className='is-centered'>
                <Column size={6}>
                    <Forecast results={results} journeyConfig={journeyConfig} />
                </Column>
                <Column size={6}>
                    <SSIBlock url={partials.MARKETING.FAI_EXTRA_BBOX} />
                </Column>
            </Columns>
        </>
    );
}
function TechnicalError({ address }: Readonly<{ address?: ReactNode }>) {
    return (
        <>
            {address}

            <Alert
                title='Aïe, nous sommes désolés. Une erreur est survenue de notre côté.'
                description='Vous pouvez modifier l’adresse pour relancer le test.'
                status={StatusState.ERROR}
                display
            />
        </>
    );
}
